<template>
  <div
    v-if="customIsVisible && videoUrl"
    :class="[backgroundColor, bottomMargin, verticalPadding, background]"
    class="youtube-video"
  >
    <div class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <div class="relative flex flex-col items-center lg:flex-row lg:gap-x-16">
        <div class="video-container w-full">
          <div v-if="!isPlaying" class="thumbnail-wrapper" @click="isPlaying = true">
            <!-- STILL IMAGE -->
            <VcImage :src="thumbnail" lazy class="rounded-lg" />
            <div class="play-button">▶</div>
          </div>
          <div v-else class="video-responsive">
            <!-- VIDEO EMBED -->
            <iframe
              :src="
                videoUrl +
                (videoUrl.includes('?') ? '&autoplay=1&modestbranding=1&rel=0' : '?autoplay=1&modestbranding=1&rel=0')
              "
              frameborder="0"
              allowfullscreen
              allow="autoplay; encrypted-media; picture-in-picture"
              class="rounded-lg"
              loading="lazy"
            ></iframe>
          </div>
        </div>

        <!-- CONTENT -->
        <div class="order-2 rounded-b-lg lg:rounded-lg lg:w-[50%] text-block">
          <div v-if="subtitle" class="mt-4 text-lg font-normal">{{ subtitle }}</div>

          <VcTypography v-if="title" :tag="heading" :anchor-id="title" class="mt-4">
            {{ title }}
          </VcTypography>

          <div v-if="contentValue" class="mt-4 text-lg block-content" v-html-safe="contentValue"></div>

          <div v-if="buttons?.length" class="my-4 flex space-x-4">
            <InnoButton
              v-for="(item, index) in buttons"
              :key="index"
              v-bind="getLinkAttr(item.link)"
              class="flex-1 lg:flex-none"
              color="primary"
              :target="'externalLink' in getLinkAttr(item.link) ? '_blank' : '_self'"
              :variant="buttons.length > 1 && index === 0 ? 'solid' : 'outline'"
              shape="pill"
              size="lg"
            >
              {{ item.label }}
            </InnoButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { getLinkAttr } from "@/core/utilities/common";
import InnoButton from "@/ui-kit/components/molecules/innovadis/inno-button.vue";

type ButtonType = {
  label: string;
  link: string;
};

interface IProps {
  heading?: string;
  title: string;
  subtitle?: string;
  text: string | { html: string };
  videoUrl: string;
  buttons?: Array<ButtonType>;

  id: string;
  background?: string;
  model: any;
  settings: any;

  customIsVisible: boolean;

  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
});

const isPlaying = ref(false);

const contentValue = computed(() => {
  return typeof props.text === "object" ? props.text?.html : props.text;
});

const thumbnail = computed(() => getYouTubeThumbnailUrl(props.videoUrl));

function getYouTubeThumbnailUrl(inputUrl: string): string {
  try {
    const url = new URL(inputUrl);

    // Extract the video ID from different possible paths
    let videoId = "";
    if (url.pathname.startsWith("/embed/")) {
      videoId = url.pathname.split("/")[2];
    } else {
      console.error("Invalid YouTube embed url:", inputUrl);
      return "";
    }
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  } catch (error) {
    console.error("Invalid YouTube embed url:", inputUrl);
    return "";
  }
}
</script>
<style scoped>
.video-container {
  position: relative;
  max-width: 100%;
}
.thumbnail-wrapper {
  position: relative;
  cursor: pointer;
  display: inline-block;
}
.thumbnail {
  width: 100%;
  height: auto;
  display: block;
}
.play-button {
  position: absolute;
  width: 6.25rem;
  height: 6.25rem;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3rem;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  padding: 0.8rem 0.8rem 0.8rem 1.2rem;
  text-align: center;
}
.video-responsive {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  overflow: hidden;
}
.video-responsive iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
